import React, { useState } from "react";

/**
 * Amazon-style sticky filters sidebar with collapsible sections
 * Displays price range, availability, discount, and subcategory filters
 */
const FiltersSidebar = ({ 
  filters, 
  onFilterChange, 
  subCategories, 
  priceRange 
}) => {
  // Track which filter sections are expanded
  const [expandedSections, setExpandedSections] = useState({
    subcategories: true,
    price: true,
    availability: true,
    discount: true,
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Price ranges - Amazon style
  const priceRanges = [
    { label: 'Under KSH 1,000', min: 0, max: 1000 },
    { label: 'KSH 1,000 - 5,000', min: 1000, max: 5000 },
    { label: 'KSH 5,000 - 10,000', min: 5000, max: 10000 },
    { label: 'KSH 10,000 - 25,000', min: 10000, max: 25000 },
    { label: 'Over KSH 25,000', min: 25000, max: Infinity },
  ];

  // Discount ranges
  const discountRanges = [
    { label: '10% off or more', value: 0.1 },
    { label: '25% off or more', value: 0.25 },
    { label: '50% off or more', value: 0.5 },
    { label: '70% off or more', value: 0.7 },
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-sm p-4 sticky top-20">
      {/* Subcategories Filter */}
      {subCategories && subCategories.length > 0 && (
        <div className="border-b border-gray-200 pb-4 mb-4">
          <button
            onClick={() => toggleSection('subcategories')}
            className="flex items-center justify-between w-full text-left"
          >
            <h3 className="text-base font-bold text-gray-900">Category</h3>
            <svg
              className={`w-4 h-4 transition-transform ${expandedSections.subcategories ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {expandedSections.subcategories && (
            <div className="mt-3 space-y-2">
              {/* All Categories option */}
              <label className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded">
                <input
                  type="checkbox"
                  checked={!filters.selectedSubCategories || filters.selectedSubCategories.length === 0}
                  onChange={() => onFilterChange({ ...filters, selectedSubCategories: [] })}
                  className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
                />
                <span className="ml-2 text-sm text-gray-700">All Categories</span>
              </label>

              {subCategories.map((subCat) => (
                <label key={subCat.subCategoryId} className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded">
                  <input
                    type="checkbox"
                    checked={filters.selectedSubCategories?.includes(subCat.subCategoryId) || false}
                    onChange={(e) => {
                      const newSelected = e.target.checked
                        ? [...(filters.selectedSubCategories || []), subCat.subCategoryId]
                        : (filters.selectedSubCategories || []).filter(id => id !== subCat.subCategoryId);
                      onFilterChange({ ...filters, selectedSubCategories: newSelected });
                    }}
                    className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">{subCat.name}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Price Filter */}
      <div className="border-b border-gray-200 pb-4 mb-4">
        <button
          onClick={() => toggleSection('price')}
          className="flex items-center justify-between w-full text-left"
        >
          <h3 className="text-base font-bold text-gray-900">Price</h3>
          <svg
            className={`w-4 h-4 transition-transform ${expandedSections.price ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        {expandedSections.price && (
          <div className="mt-3 space-y-2">
            {priceRanges.map((range, index) => (
              <label key={index} className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded">
                <input
                  type="radio"
                  name="priceRange"
                  checked={
                    filters.priceMin === range.min && filters.priceMax === range.max
                  }
                  onChange={() => {
                    onFilterChange({
                      ...filters,
                      priceMin: range.min,
                      priceMax: range.max
                    });
                  }}
                  className="w-4 h-4 text-orange-500 border-gray-300 focus:ring-orange-500"
                />
                <span className="ml-2 text-sm text-gray-700">{range.label}</span>
              </label>
            ))}
            
            {/* Custom Price Range */}
            <div className="pt-2 mt-2 border-t border-gray-100">
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  placeholder="Min"
                  value={filters.customPriceMin || ''}
                  onChange={(e) => onFilterChange({
                    ...filters,
                    customPriceMin: e.target.value ? Number(e.target.value) : null,
                    priceMin: null,
                    priceMax: null
                  })}
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                />
                <span className="text-gray-500">-</span>
                <input
                  type="number"
                  placeholder="Max"
                  value={filters.customPriceMax || ''}
                  onChange={(e) => onFilterChange({
                    ...filters,
                    customPriceMax: e.target.value ? Number(e.target.value) : null,
                    priceMin: null,
                    priceMax: null
                  })}
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
              <button
                onClick={() => {
                  if (filters.customPriceMin || filters.customPriceMax) {
                    onFilterChange({
                      ...filters,
                      priceMin: filters.customPriceMin || 0,
                      priceMax: filters.customPriceMax || Infinity
                    });
                  }
                }}
                className="w-full mt-2 px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-800 rounded transition-colors"
              >
                Go
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Availability Filter */}
      <div className="border-b border-gray-200 pb-4 mb-4">
        <button
          onClick={() => toggleSection('availability')}
          className="flex items-center justify-between w-full text-left"
        >
          <h3 className="text-base font-bold text-gray-900">Availability</h3>
          <svg
            className={`w-4 h-4 transition-transform ${expandedSections.availability ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        {expandedSections.availability && (
          <div className="mt-3 space-y-2">
            <label className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded">
              <input
                type="checkbox"
                checked={filters.inStockOnly || false}
                onChange={(e) => onFilterChange({
                  ...filters,
                  inStockOnly: e.target.checked
                })}
                className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500"
              />
              <span className="ml-2 text-sm text-gray-700">In Stock Only</span>
            </label>
          </div>
        )}
      </div>

      {/* Discount Filter */}
      <div className="pb-4">
        <button
          onClick={() => toggleSection('discount')}
          className="flex items-center justify-between w-full text-left"
        >
          <h3 className="text-base font-bold text-gray-900">Deals & Discounts</h3>
          <svg
            className={`w-4 h-4 transition-transform ${expandedSections.discount ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        {expandedSections.discount && (
          <div className="mt-3 space-y-2">
            {discountRanges.map((range, index) => (
              <label key={index} className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded">
                <input
                  type="radio"
                  name="discount"
                  checked={filters.minDiscount === range.value}
                  onChange={() => onFilterChange({
                    ...filters,
                    minDiscount: range.value
                  })}
                  className="w-4 h-4 text-orange-500 border-gray-300 focus:ring-orange-500"
                />
                <span className="ml-2 text-sm text-gray-700">{range.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Clear All Filters */}
      {(filters.selectedSubCategories?.length > 0 || 
        filters.priceMin || 
        filters.priceMax || 
        filters.inStockOnly || 
        filters.minDiscount) && (
        <button
          onClick={() => onFilterChange({
            selectedSubCategories: [],
            priceMin: null,
            priceMax: null,
            customPriceMin: null,
            customPriceMax: null,
            inStockOnly: false,
            minDiscount: null
          })}
          className="w-full mt-4 px-4 py-2 text-sm text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded transition-colors border border-orange-300"
        >
          Clear all filters
        </button>
      )}
    </div>
  );
};

export default FiltersSidebar;
